import {ChangeDetectionStrategy, Component, inject, viewChild} from '@angular/core';
import {BaseModalComponent} from "../base-modal/base-modal.component";
import {MatButton} from "@angular/material/button";
import {MatStep, MatStepper} from "@angular/material/stepper";
import {FormGroup} from "@angular/forms";
import {TelegramAccountsService} from "./services/telegram-accounts.service";
import {GetTelegramCodeParams, SetTelegramCodeParams} from "./types/add-telegram-accounts.types";
import {
  addTelegramAccountCodeFormFields,
  addTelegramAccountFormFields
} from "./configs/add-telegram-account-form-config";
import {addTelegramAccountModalName} from "./configs/add-telegram-account-config";
import {
  AddTelegramAccountFormComponent
} from "./components/add-telegram-account-form/add-telegram-account-form.component";
import {AddTelegramAccountFormField} from "./types/add-telegram-account-form.types";

@Component({
  selector: 'app-add-telegram-account-modal',
  standalone: true,
  imports: [
    BaseModalComponent,
    MatButton,
    MatStep,
    MatStepper,
    AddTelegramAccountFormComponent,
  ],
  templateUrl: './add-telegram-account-modal.component.html',
  styleUrl: './add-telegram-account-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddTelegramAccountModalComponent extends BaseModalComponent{
  stepper = viewChild<MatStepper>('stepper')

  private telegramAccountsService: TelegramAccountsService = inject(TelegramAccountsService);

  public readonly addTelegramAccountCodeFormFields = addTelegramAccountCodeFormFields;
  public readonly addTelegramAccountFormFields = addTelegramAccountFormFields;
  public isLinear = false;
  public formGroup: FormGroup = new FormGroup({});
  public codeFormGroup: FormGroup = new FormGroup({});

  public override ngOnInit(): void {
    this.isMobile = this.deviceService.isMobile;
    this.modalStatesService.openModal(addTelegramAccountModalName);
    this.formGroup = this.baseModalService.getFormGroup<AddTelegramAccountFormField>(addTelegramAccountFormFields);
    this.codeFormGroup = this.baseModalService.getFormGroup<AddTelegramAccountFormField>(addTelegramAccountCodeFormFields);
    this.initIsMobileSubscription();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(addTelegramAccountModalName);
  }

  public isDisabledBtn(currentStep: number): boolean {
    return currentStep === 0 ? this.formGroup.invalid : this.codeFormGroup.invalid;
  }

  public async handleBtnClick(currentStep: number): Promise<void> {
    const handler: Record<number, () => Promise<void>> = {
      0: () => this.handleGetCode(),
      1: () => this.handleSetCode()
    };

    await handler[currentStep]?.();
  }

  private async handleGetCode(): Promise<void> {
    const getTelegramCodeParams: GetTelegramCodeParams = {
      phone: this.formGroup.get('phone' as string)?.value,
      accountId: this.formGroup.get('accountId' as string)?.value
    };

    await this.telegramAccountsService.getTelegramCode(getTelegramCodeParams)
      .then(() => {
        this.codeFormGroup.get('accountId'as string)?.setValue(this.formGroup.get('accountId'as string)?.value);
        this.stepper()?.next();
      });
  }

  private async handleSetCode() {
    const setTelegramCodeParams: SetTelegramCodeParams = {
      code: this.codeFormGroup.get('code' as string)?.value,
      accountId: this.formGroup.get('accountId' as string)?.value
    };

    await this.telegramAccountsService.setTelegramCode(setTelegramCodeParams)
      .then(() => {
        this.stepper()?.next();
        this.createTelegramAccount();
      });
  }

  private initIsMobileSubscription(): void {
    this.subscriptions.add(
      this.deviceService.isMobile$.subscribe((isMobile: boolean) => {
        this.isMobile = isMobile;
      })
    );
  }

  private async createTelegramAccount(): Promise<void> {
    await this.telegramAccountsService.createTelegramAccount(this.formGroup.value);
  }
}
